import { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FiFileText,
  FiUser,
  FiEye,
  FiDownload,
  FiMail,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiInfo,
  FiFolder,
  FiEdit,
  FiX,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";
import {
  MdOutlinePendingActions,
  MdOutlineGavel,
  MdOutlineEventAvailable
} from "react-icons/md";
import "../styles/UserAgreements.css";

/* ======================
   AGREEMENT STATUS CONFIGURATION
====================== */
const AGREEMENT_STATUS_CONFIG = {
  SIGNED_BY_SOLICITOR: {
    label: "Awaiting Your Signature",
    icon: <FiClock size={14} />,
    color: "#f59e0b",
    bgColor: "#fef3c7",
    borderColor: "#fde68a"
  },
  SIGNED_BY_BOTH: {
    label: "Fully Executed",
    icon: <FiCheckCircle size={14} />,
    color: "#16a34a",
    bgColor: "#dcfce7",
    borderColor: "#bbf7d0"
  },
  PENDING_REVIEW: {
    label: "Under Review",
    icon: <MdOutlinePendingActions size={14} />,
    color: "#64748b",
    bgColor: "#f1f5f9",
    borderColor: "#cbd5e1"
  },
  EXPIRED: {
    label: "Expired",
    icon: <FiX size={14} />,
    color: "#6b7280",
    bgColor: "#f3f4f6",
    borderColor: "#e5e7eb"
  }
};



export default function UserAgreements({ onSign }) {
  const { token } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [refreshLoading, setRefreshLoading] = useState(false);

  /* ======================
     FETCH AGREEMENTS
  ====================== */
  const fetchAgreements = async () => {
    try {
      setRefreshLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/agreements/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to fetch agreements");

      const data = await res.json();
      setAgreements(data.agreements || []);
    } catch (error) {
      console.error("Fetch agreements error:", error);
      toast.error("Failed to load agreements");
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ======================
     AGREEMENT ACTIONS
  ====================== */
  const handleView = async (agreementId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/user/download/${agreementId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) throw new Error("Failed to fetch PDF");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      // 🔥 Open in new tab (VIEW MODE)
      window.open(url, "_blank");

      setTimeout(() => window.URL.revokeObjectURL(url), 2000);

    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to view agreement PDF");
    }
  };


  const handleDownload = async (agreementId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/user/download/${agreementId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${agreementId}.pdf`;   // 🔥 Proper filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success("Agreement downloaded successfully");

    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download agreement");
    }
  };


  const handleEmail = async (agreementId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/user/mail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ agreementId })
        }
      );

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Email failed");

      toast.success("Agreement sent to your email successfully");

    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send agreement via email");
    }
  };


  const handleSign = (agreementId) => {
    Swal.fire({
      title: "Ready to Sign?",
      html: "You will be redirected to the signing interface to execute this agreement.",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#d6b87c",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Proceed to Sign",
      cancelButtonText: "Cancel",
      width: "500px"
    }).then((result) => {
      if (result.isConfirmed) {
        onSign(agreementId);
      }
    });
  };

  /* ======================
     MODAL & SLIDER FUNCTIONS
  ====================== */
  const openModal = (agreement) => {
    setSelectedAgreement(agreement);
    setShowModal(true);
    setCurrentSlide(0);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAgreement(null);
    setCurrentSlide(0);
  };

  const nextSlide = () => {
    const maxSlides = 3;
    setCurrentSlide((prev) => (prev + 1) % maxSlides);
  };

  const prevSlide = () => {
    const maxSlides = 3;
    setCurrentSlide((prev) => (prev - 1 + maxSlides) % maxSlides);
  };

  /* ======================
     RENDER STATUS BADGE
  ====================== */
  const renderStatusBadge = (status) => {
    const config = AGREEMENT_STATUS_CONFIG[status] || {
      label: status,
      icon: <FiAlertCircle size={14} />,
      color: "#6b7280",
      bgColor: "#f3f4f6",
      borderColor: "#e5e7eb"
    };

    return (
      <span
        className="user-agreements-status-badge"
        style={{
          backgroundColor: config.bgColor,
          color: config.color,
          border: `1px solid ${config.borderColor}`
        }}
      >
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  /* ======================
     STATS CALCULATION
  ====================== */
  const pendingAgreements = agreements.filter(
    ag => ag.status === "SIGNED_BY_SOLICITOR"
  );

  const completedAgreements = agreements.filter(
    ag => ag.status === "SIGNED_BY_BOTH"
  );

  const stats = {
    pending: pendingAgreements.length,
    completed: completedAgreements.length,
    total: agreements.length
  };

  const visibleAgreements = activeTab === "PENDING" ? pendingAgreements : completedAgreements;

  /* ======================
     RENDER SLIDE CONTENT
  ====================== */
  const renderSlideContent = () => {
    if (!selectedAgreement) return null;

    const slides = [
      {
        title: "Agreement Details",
        icon: <FiInfo size={16} />,
        content: (
          <div className="user-agreements-details-grid">
            <div className="user-agreements-detail-item">
              <strong>Agreement ID:</strong>
              <span>{selectedAgreement.agreementId}</span>
            </div>
            <div className="user-agreements-detail-item">
              <strong>Case Type:</strong>
              <span>{selectedAgreement.caseType}</span>
            </div>
            <div className="user-agreements-detail-item">
              <strong>Created Date:</strong>
              <span>{new Date(selectedAgreement.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="user-agreements-detail-item">
              <strong>Last Updated:</strong>
              <span>{new Date(selectedAgreement.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        )
      },
      {
        title: "Solicitor Information",
        icon: <FiUser size={16} />,
        content: (
          <div className="user-agreements-details-grid">
            <div className="user-agreements-detail-item">
              <strong>Name:</strong>
              <span>{selectedAgreement.solicitorDetails?.name || "Not assigned"}</span>
            </div>
            <div className="user-agreements-detail-item">
              <strong>Email:</strong>
              <span>{selectedAgreement.solicitorDetails?.email || "N/A"}</span>
            </div>
            <div className="user-agreements-detail-item">
              <strong>Phone:</strong>
              <span>
                {selectedAgreement.solicitorDetails?.countryCode && selectedAgreement.solicitorDetails?.mobile
                  ? `${selectedAgreement.solicitorDetails.countryCode} ${selectedAgreement.solicitorDetails.mobile}`
                  : "N/A"}
              </span>
            </div>
          </div>
        )
      },
      {
        title: "Document Status",
        icon: <FiFileText size={16} />,
        content: (
          <div className="user-agreements-details-grid">
            <div className="user-agreements-detail-item">
              <strong>Status:</strong>
              {renderStatusBadge(selectedAgreement.status)}
            </div>
            <div className="user-agreements-detail-item">
              <strong>PDF Available:</strong>
              <span>{selectedAgreement.pdfUrl ? "Yes" : "No"}</span>
            </div>
          </div>
        )
      }
    ];

    return (
      <div className="user-agreements-slide">
        <h4 className="user-agreements-slide-title">
          {slides[currentSlide].icon}
          <span>{slides[currentSlide].title}</span>
        </h4>
        <div className="user-agreements-slide-content">
          {slides[currentSlide].content}
        </div>
      </div>
    );
  };

  return (
    <div className="premium-user-agreements">
      {/* ======================
               HEADER
      ====================== */}
      <div className="user-agreements-header">
        <div className="user-agreements-header-left">
          <h1 className="user-agreements-page-title">My Legal Agreements</h1>
          <p className="user-agreements-page-subtitle">
            Review, sign, and manage your legal agreements
          </p>
        </div>
        <div className="user-agreements-header-right">
          <button
            className="user-agreements-refresh-btn"
            onClick={fetchAgreements}
            disabled={refreshLoading}
          >
            <FiRefreshCw size={18} className={refreshLoading ? "spinning" : ""} />
            <span>{refreshLoading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* ======================
               STATS CARDS
      ====================== */}
      <div className="user-agreements-stats-grid">
        <div className="user-agreements-stat-card user-agreements-stat-pending">
          <div className="user-agreements-stat-icon">
            <FiClock size={24} />
          </div>
          <div className="user-agreements-stat-content">
            <h3 className="user-agreements-stat-value">{stats.pending}</h3>
            <p className="user-agreements-stat-label">Pending Signature</p>
            <span className="user-agreements-stat-subtext">Awaiting Your Action</span>
          </div>
        </div>

        <div className="user-agreements-stat-card user-agreements-stat-completed">
          <div className="user-agreements-stat-icon">
            <FiCheckCircle size={24} />
          </div>
          <div className="user-agreements-stat-content">
            <h3 className="user-agreements-stat-value">{stats.completed}</h3>
            <p className="user-agreements-stat-label">Completed</p>
            <span className="user-agreements-stat-subtext">Fully Executed</span>
          </div>
        </div>

        <div className="user-agreements-stat-card user-agreements-stat-total">
          <div className="user-agreements-stat-icon">
            <FiFolder size={24} />
          </div>
          <div className="user-agreements-stat-content">
            <h3 className="user-agreements-stat-value">{stats.total}</h3>
            <p className="user-agreements-stat-label">Total Agreements</p>
            <span className="user-agreements-stat-subtext">All Legal Agreements</span>
          </div>
        </div>
      </div>

      {/* ======================
               MAIN CONTENT
      ====================== */}
      <div className="user-agreements-content">
        {/* Tabs */}
        <div className="user-agreements-tabs-container">
          <div className="user-agreements-main-tabs">
            <button
              className={`user-agreements-tab-btn ${activeTab === "PENDING" ? "user-agreements-tab-active" : ""}`}
              onClick={() => setActiveTab("PENDING")}
            >
              <FiClock size={16} />
              <span>Pending ({stats.pending})</span>
            </button>

            <button
              className={`user-agreements-tab-btn ${activeTab === "COMPLETED" ? "user-agreements-tab-active" : ""}`}
              onClick={() => setActiveTab("COMPLETED")}
            >
              <FiCheckCircle size={16} />
              <span>Completed ({stats.completed})</span>
            </button>
          </div>
        </div>

        {/* Agreements List */}
        <div className="user-agreements-list-container">
          {loading ? (
            <div className="user-agreements-loading-state">
              <div className="user-agreements-loading-spinner"></div>
              <p>Loading agreements...</p>
            </div>
          ) : visibleAgreements.length === 0 ? (
            <div className="user-agreements-empty-state">
              <MdOutlineEventAvailable size={48} />
              <p>No agreements found</p>
              <small>
                {activeTab === "PENDING"
                  ? "All agreements are signed and complete"
                  : "No completed agreements yet"}
              </small>
            </div>
          ) : (
            <div className="user-agreements-cards-grid">
              {visibleAgreements.map((ag) => (
                <div key={ag._id} className="user-agreements-card">
                  {/* Card Header */}
                  <div className="user-agreements-card-header">
                    <div className="user-agreements-agreement-info">
                      <div className="user-agreements-agreement-avatar">
                        <FiFileText size={20} />
                      </div>
                      <div className="user-agreements-agreement-details">
                        <div className="user-agreements-agreement-id">
                          Agreement: {ag.agreementId}
                        </div>
                        <div className="user-agreements-solicitor-name">
                          <FiUser size={12} />
                          <span>{ag.solicitorDetails?.name || "No Solicitor Assigned"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="user-agreements-status-container">
                      {renderStatusBadge(ag.status)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="user-agreements-card-body">
                    <div className="user-agreements-meta-info">
                      <div className="user-agreements-type">
                        <MdOutlineGavel size={14} />
                        <span>{ag.caseType || "General Legal Matter"}</span>
                      </div>
                      <div className="user-agreements-date">
                        <FiCalendar size={14} />
                        <span>Created: {new Date(ag.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="user-agreements-actions">
                      <button
                        className="user-agreements-view-details-btn"
                        onClick={() => openModal(ag)}
                      >
                        <FiEye size={14} />
                        <span>View Details</span>
                      </button>

                      {activeTab === "PENDING" ? (
                        <button
                          className="user-agreements-sign-btn"
                          onClick={() => handleSign(ag.agreementId)}
                        >
                          <FiEdit size={14} />
                          <span>Sign Agreement</span>
                        </button>
                      ) : (
                        <div className="user-agreements-action-group">
                          <button
                            className="user-agreements-action-btn secondary"
                            style={{ height: "40px", width: "60px" }}
                            onClick={() => handleView(ag.agreementId)}
                            title="View PDF"
                          >
                            <FiEye size={18} />
                          </button>
                          <button
                            className="user-agreements-action-btn secondary"
                            style={{ height: "40px", width: "60px" }}
                            onClick={() => handleDownload(ag.agreementId)}
                            title="Download PDF"
                          >
                            <FiDownload size={14} />
                          </button>
                          <button
                            className="user-agreements-action-btn secondary"
                            style={{ height: "40px", width: "60px" }}
                            onClick={() => handleEmail(ag.agreementId)}
                            title="Email Copy"
                          >
                            <FiMail size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ======================
               MODAL
      ====================== */}
      {showModal && selectedAgreement && (
        <div className="user-agreements-modal-overlay">
          <div className="user-agreements-modal">
            <div className="user-agreements-modal-header">
              <h3>Agreement Details</h3>
              <button
                className="user-agreements-modal-close"
                onClick={closeModal}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="user-agreements-modal-body">
              {/* Slide Content */}
              {renderSlideContent()}

              {/* Navigation */}
              <div className="user-agreements-slider-navigation">
                <button
                  className="user-agreements-slider-nav-btn"
                  onClick={prevSlide}
                >
                  <FiChevronLeft size={16} />
                  <span>Previous</span>
                </button>

                <div className="user-agreements-slider-dots">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      className={`user-agreements-slider-dot ${currentSlide === index ? "active" : ""}`}
                      onClick={() => setCurrentSlide(index)}
                    />
                  ))}
                </div>

                <button
                  className="user-agreements-slider-nav-btn"
                  onClick={nextSlide}
                >
                  <span>Next</span>
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="user-agreements-modal-footer">
              <div className="user-agreements-modal-actions">
                {activeTab === "PENDING" ? (
                  <button
                    className="user-agreements-modal-primary-btn"
                    onClick={() => {
                      closeModal();
                      handleSign(selectedAgreement.agreementId);
                    }}
                  >
                    <FiEdit size={16} />
                    <span>Sign This Agreement</span>
                  </button>
                ) : (
                  <>
                    <button
                      className="user-agreements-modal-secondary-btn"
                      onClick={() => handleView(selectedAgreement.agreementId)}
                    >
                      <FiEye size={16} />
                      <span>View PDF</span>
                    </button>
                    <button
                      className="user-agreements-modal-secondary-btn"
                      onClick={() => handleDownload(selectedAgreement.agreementId)}
                    >
                      <FiDownload size={16} />
                      <span>Download</span>
                    </button>
                    <button
                      className="user-agreements-modal-secondary-btn"
                      onClick={() => handleEmail(selectedAgreement.agreementId)}
                    >
                      <FiMail size={16} />
                      <span>Email Copy</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}